import { useHead, useSeoMeta } from "@unhead/react"
import Navbar from "@/components/Navbar"
import CowCalfCostCalculator from "@/components/CowCalfCostCalculator"
import CowCalfProfitGuide from "@/components/CowCalfProfitGuide"
import AboutSection from "@/components/AboutSection"
import QuickLinksFooter from "@/components/QuickLinksFooter"

function CowCalfCost() {
  useSeoMeta({
    title: 'Cow-Calf Cost of Production Calculator | Free Tool',
    description: "Break down your cow calf cost of production with our free calculator. Analyze 11 expense categories including feed, vet, labor, and land costs. Calculate cost per calf weaned and cost per pound of calf produced.",
    ogType: 'website',
    ogTitle: 'Cow-Calf Cost of Production Calculator | Free Tool',
    ogDescription: "Analyze your cow-calf cost of production across 11 expense categories. Calculate cost per calf weaned and per lb produced.",
    ogImage: 'https://www.cowcalculator.com/og-gestation.jpg',
    ogUrl: 'https://www.cowcalculator.com/cow-calf-cost-of-production',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Cow-Calf Cost of Production Calculator | Free Tool',
    twitterDescription: "Analyze your cow-calf cost of production across 11 expense categories. Calculate cost per calf weaned and per lb produced.",
    twitterImage: 'https://www.cowcalculator.com/og-gestation.jpg',
  })

  useHead({
    link: [{ rel: 'canonical', href: 'https://www.cowcalculator.com/cow-calf-cost-of-production' }],
  })

  return (
    <div className="min-h-screen bg-[#FEFAE0]">
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative bg-[#283618] overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: "radial-gradient(circle at 25% 50%, #606C38 1px, transparent 1px), radial-gradient(circle at 75% 30%, #606C38 1px, transparent 1px)",
              backgroundSize: "50px 50px, 70px 70px",
            }} />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 text-center">
            <div className="inline-flex items-center gap-2 bg-[#606C38]/30 backdrop-blur-sm border border-[#606C38]/40 rounded-full px-4 py-1.5 mb-5">
              <div className="w-2 h-2 rounded-full bg-[#B7E4C7] animate-pulse" />
              <span className="text-[#B7E4C7] text-xs sm:text-sm font-medium tracking-wide">
                Cost Analysis Tool
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FEFAE0] leading-tight">
              Cow Calf Cost of{" "}
              <span className="text-[#D4A373]">Production</span>
            </h1>
            <p className="mt-4 sm:mt-5 text-base sm:text-lg text-[#B7E4C7]/90 max-w-2xl mx-auto leading-relaxed">
              Analyze your <strong>cost of production per calf</strong> with a detailed 11-category expense breakdown. Identify your highest-impact costs and find where to save.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
              {[
                { value: "Free", label: "No Cost" },
                { value: "11", label: "Cost Categories" },
                { value: "4", label: "Regional Presets" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-[#D4A373]">{stat.value}</div>
                  <div className="text-xs text-[#B7E4C7]/60 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 48" fill="none" className="w-full">
              <path d="M0 48L60 40C120 32 240 16 360 12C480 8 600 16 720 20C840 24 960 24 1080 20C1200 16 1320 8 1380 4L1440 0V48H0Z" fill="#FEFAE0" />
            </svg>
          </div>
        </section>

        <CowCalfCostCalculator />
        <CowCalfProfitGuide />
        <AboutSection />
      </main>
      <QuickLinksFooter />
    </div>
  )
}

export default CowCalfCost
